import React, { useEffect, useRef } from 'react';
import { Alert } from 'antd';
import { Auth } from 'aws-amplify';
import aws_exports from '../../../../../aws-exports';

const styles = {
  wrap: { marginTop: 12 },
};

const EMBED_SCRIPT_ID = 'mj-widgets-embed';

// Orgs white-label this; setSystemColor writes the live value to the document root.
const DEFAULT_PRIMARY = '#1890ff';

function applyContext(host, context) {
  Object.entries(context).forEach(([name, value]) => {
    if (value === null || value === undefined || value === '') {
      host.removeAttribute(name);
    } else {
      host.setAttribute(name, value);
    }
  });
}

function resolvePrimaryColor() {
  try {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--ant-primary-color')
      .trim();
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value) ? value : DEFAULT_PRIMARY;
  } catch (err) {
    return DEFAULT_PRIMARY;
  }
}

// Host for the `media-plan` widget from @mediajel/widgets. Mounts the element and
// hands it the campaign's context; all plan UI lives in the widget.
export default function MediaPlanWidget({
  objective,
  budget,
  startDate,
  endDate,
  advertiserId,
  advertiserName,
  campaignName,
  advertiserDomain,
  category,
  city,
  geo,
}) {
  const containerRef = useRef(null);
  const hostRef = useRef(null);

  const widgetsUrl = process.env.REACT_APP_WIDGETS_URL;

  // gql-service, not external-service: it proxies the call and adds the service
  // token server-side, so no long-lived credential reaches the browser.
  const apiBase = (process.env.REACT_APP_HTTP_URI || '')
    .replace(/\/graphql\/?$/, '')
    .replace(/\/$/, '');

  const configured = !!(widgetsUrl && apiBase);

  const toDate = (value) => {
    if (!value) return null;
    return typeof value === 'string' ? value : value.format('YYYY-MM-DD');
  };

  const start = toDate(startDate);
  const end = toDate(endDate);

  const context = {
    'data-objective': objective || 'traffic',
    'data-budget': budget > 0 ? String(budget) : null,
    'data-start-date': start,
    'data-end-date': end,
    'data-advertiser-id': advertiserId,
    'data-advertiser-name': advertiserName,
    'data-campaign-name': campaignName,
    'data-advertiser-domain': advertiserDomain,
    'data-category': category,
    'data-auto-signals': category ? 'true' : null,
    'data-city': city,
    'data-geo': geo,
  };

  const contextKey = JSON.stringify(context);

  // A boolean, not the values: once true, editing the budget updates an
  // attribute instead of remounting the widget.
  const ready = !!(budget > 0 && start && end);

  // Mount once. Form values live in the second effect — remounting per keystroke
  // would discard whatever allocation Ad Ops had adjusted inside the widget.
  useEffect(() => {
    if (!configured || !ready || !containerRef.current) return undefined;

    const container = containerRef.current;
    let cancelled = false;

    const host = document.createElement('div');
    host.setAttribute('data-widget', 'media-plan');
    host.setAttribute('data-api-base', apiBase);
    applyContext(host, context);
    container.appendChild(host);
    hostRef.current = host;

    const mount = async () => {
      if (cancelled || !window.__MJ_WIDGETS) return;

      try {
        const [session, currentUser, creds] = await Promise.all([
          Auth.currentSession(),
          Auth.currentAuthenticatedUser(),
          Auth.currentCredentials(),
        ]);
        const { aws_cognito_region, aws_user_pools_id } = aws_exports;

        if (cancelled) return;

        window.__MJ_WIDGETS.setHeaders({
          jwtToken: session.idToken.jwtToken,
          identity: creds.identityId,
          provider: `cognito-idp.${aws_cognito_region}.amazonaws.com/${aws_user_pools_id}`,
          username: currentUser.username,
        });
      } catch (err) {
        // Guest user — the proxy answers 401 and the widget shows the error.
      }

      if (cancelled) return;

      window.__MJ_WIDGETS.setApiBase(apiBase);
      window.__MJ_WIDGETS.setPrimaryColor(resolvePrimaryColor());
      window.__MJ_WIDGETS.init();
    };

    let script = document.getElementById(EMBED_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = EMBED_SCRIPT_ID;
      script.src = `${widgetsUrl.replace(/\/$/, '')}/embed.js`;
      script.async = true;
      script.addEventListener('load', mount);
      document.body.appendChild(script);
    } else if (window.__MJ_WIDGETS) {
      mount();
    } else {
      script.addEventListener('load', mount);
    }

    return () => {
      cancelled = true;
      script.removeEventListener('load', mount);
      if (host.parentNode === container) container.removeChild(host);
      hostRef.current = null;
    };
    // context is applied once here and kept current by the effect below;
    // listing it would reintroduce the remount-per-keystroke this splits apart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured, ready, widgetsUrl, apiBase]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    applyContext(host, context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  // One stable root, always mounted, React never renders into the container.
  // Returning different roots per state swaps the subtree under the effect's
  // node and throws "Failed to execute 'removeChild' on 'Node'".
  return (
    <div style={styles.wrap}>
      {!configured && (
        <Alert
          type="info"
          showIcon
          message="Media plan widget is not configured"
          description="Set REACT_APP_WIDGETS_URL (and REACT_APP_HTTP_URI) to enable it."
        />
      )}

      {configured && !ready && (
        <Alert
          type="info"
          showIcon
          message="Set a budget and flight dates to generate a media plan."
        />
      )}

      <div ref={containerRef} />
    </div>
  );
}
